import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUnlockedStages, STAGE_ORDER, type Stage } from '@piperix/shared/stages';
import { apiClient } from '../api/client';
import { ObjectCanvas, readFloorLabel, type ObjectCanvasView } from '../editor/ObjectCanvas';

type ProjectDocumentPayload = {
  name: string;
  currentTab?: unknown;
  completedStages?: unknown;
  invalidatedFromStage?: unknown;
  floors: unknown[];
  [key: string]: unknown;
};

type ProjectDocument = {
  name: string;
  currentTab: Stage;
  completedStages: Stage[];
  invalidatedFromStage: Stage | null;
  floors: unknown[];
  [key: string]: unknown;
};

type ProjectResponsePayload = {
  id: string;
  name: string;
  document: ProjectDocumentPayload;
  version: number;
};

type ProjectResponse = {
  id: string;
  name: string;
  document: ProjectDocument;
  version: number;
};

type SaveTrigger = 'auto' | 'manual';

const TAB_LABELS: Record<Stage, string> = {
  object: 'Объект',
  devices: 'Приборы',
  pipes: 'Трубы',
  estimates: 'Сметы',
};

const TAB_PLACEHOLDERS: Record<Stage, string> = {
  object: 'Раздел «Объект»: заглушка. Здесь будет настройка объекта.',
  devices: 'Раздел «Приборы»: заглушка. Здесь будет подбор и размещение приборов.',
  pipes: 'Раздел «Трубы»: заглушка. Здесь будет трассировка труб.',
  estimates: 'Раздел «Сметы»: заглушка. Здесь будут результаты расчёта и смета.',
};

function StageLockIcon() {
  return (
    <svg className="editor-tab__icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 1.25A3.25 3.25 0 0 0 4.75 4.5V6.5h-.75A1.75 1.75 0 0 0 2.25 8.25v5A1.75 1.75 0 0 0 4 15h8a1.75 1.75 0 0 0 1.75-1.75v-5A1.75 1.75 0 0 0 12 6.5h-.75V4.5A3.25 3.25 0 0 0 8 1.25Zm0 1.5a1.75 1.75 0 0 1 1.75 1.75V6.5h-3.5V4.5A1.75 1.75 0 0 1 8 2.75ZM4 8h8a.25.25 0 0 1 .25.25v5a.25.25 0 0 1-.25.25H4a.25.25 0 0 1-.25-.25v-5A.25.25 0 0 1 4 8Z"
      />
    </svg>
  );
}

function StageWarningIcon() {
  return (
    <svg className="editor-tab__icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        fill="#f59e0b"
        d="M7.15 1.72a1 1 0 0 1 1.7 0l6.02 10.04A1 1 0 0 1 14.02 13.3H1.98a1 1 0 0 1-.85-1.54L7.15 1.72Z"
      />
      <path fill="#92400e" d="M7.25 5.4h1.5V9.2h-1.5V5.4Zm0 4.7h1.5v1.5h-1.5v-1.5Z" />
    </svg>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.response?.status === 401) {
      return 'Сессия истекла. Войдите в систему заново.';
    }

    if (error.response?.status === 404) {
      return 'Проект не найден.';
    }
  }

  return 'Не удалось загрузить проект. Попробуйте ещё раз.';
}

function getSyncErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.response?.status === 422) {
      const detail = error.response.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return `Ошибка валидации (422): ${detail}`;
      }
      return 'Сервер отклонил документ (422). Проверьте структуру данных проекта.';
    }

    if (error.response?.status === 409) {
      return 'Конфликт версий (409): проект изменился в другой сессии. Перезагрузите страницу.';
    }

    if (error.response?.status === 401) {
      return 'Сессия истекла. Войдите в систему заново.';
    }
  }

  return 'Не удалось сохранить изменения. Попробуйте ещё раз.';
}

function normalizeTab(tab: unknown): Stage {
  return STAGE_ORDER.includes(tab as Stage) ? (tab as Stage) : 'object';
}

function normalizeCompletedStages(value: unknown): Stage[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((stage): stage is Stage => STAGE_ORDER.includes(stage as Stage));
}

function normalizeInvalidatedFromStage(value: unknown): Stage | null {
  if (typeof value !== 'string') {
    return null;
  }

  return STAGE_ORDER.includes(value as Stage) ? (value as Stage) : null;
}

function normalizeDocument(document: ProjectDocumentPayload): ProjectDocument {
  return {
    ...document,
    currentTab: normalizeTab(document.currentTab),
    completedStages: normalizeCompletedStages(document.completedStages),
    invalidatedFromStage: normalizeInvalidatedFromStage(document.invalidatedFromStage),
  };
}

/** Вкладка открыта, если она в обычном префиксе или в хвосте, который прислал сервер. */
function isStageOpen(
  stage: Stage,
  completedStages: readonly Stage[],
  invalidatedFromStage: Stage | null,
): boolean {
  if (getUnlockedStages(completedStages).includes(stage)) {
    return true;
  }

  if (invalidatedFromStage === null) {
    return false;
  }

  return STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf(invalidatedFromStage);
}

/** Пометка с сервера: этот этап и все после него нужно подтвердить снова. */
function stageNeedsConfirm(stage: Stage, invalidatedFromStage: Stage | null): boolean {
  if (invalidatedFromStage === null) {
    return false;
  }

  return STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf(invalidatedFromStage);
}

/** «Сохранить этап» сдвигает пометку на один шаг. На последнем этапе она снимается. */
function invalidatedAfterSave(savedStage: Stage, invalidatedFromStage: Stage | null): Stage | null {
  if (invalidatedFromStage !== savedStage) {
    return invalidatedFromStage;
  }

  const nextIndex = STAGE_ORDER.indexOf(savedStage) + 1;
  if (nextIndex >= STAGE_ORDER.length) {
    return null;
  }

  return STAGE_ORDER[nextIndex];
}

function getNextStage(stage: Stage): Stage {
  const currentIndex = STAGE_ORDER.indexOf(stage);
  if (currentIndex < 0 || currentIndex >= STAGE_ORDER.length - 1) {
    return stage;
  }

  return STAGE_ORDER[currentIndex + 1];
}

function getRollbackCompletedStages(targetStage: Stage): Stage[] {
  const targetIndex = STAGE_ORDER.indexOf(targetStage);
  if (targetIndex <= 0) {
    return [];
  }

  return STAGE_ORDER.slice(0, targetIndex);
}

export function ProjectEditorPage() {
  const { id } = useParams();
  const [project, setProject] = useState<ProjectResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectRef = useRef<ProjectResponse | null>(null);
  const isSavingRef = useRef(false);
  const pendingSyncRef = useRef(false);
  const pendingTriggerRef = useRef<SaveTrigger>('auto');
  const autosaveBlockedRef = useRef(false);
  /** «Сохранить этап» открыл следующий шаг: успех этой записи не показывать как «Сохранено.». */
  const hideAdvancedStageSuccessRef = useRef(false);
  const [objectViewState, setObjectViewState] = useState<{
    projectId: string | undefined;
    view: ObjectCanvasView;
  }>({ projectId: id, view: { zoom: 1, x: 0, y: 0 } });
  if (objectViewState.projectId !== id) {
    setObjectViewState({ projectId: id, view: { zoom: 1, x: 0, y: 0 } });
  }
  const objectView = objectViewState.view;

  function setObjectView(view: ObjectCanvasView) {
    setObjectViewState({ projectId: id, view });
  }

  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  const runSync = useCallback(
    async (trigger: SaveTrigger): Promise<void> => {
      const hideSuccess = hideAdvancedStageSuccessRef.current;
      hideAdvancedStageSuccessRef.current = false;

      if (!id) {
        return;
      }

      const snapshot = projectRef.current;
      if (!snapshot) {
        return;
      }

      isSavingRef.current = true;
      setIsSaving(true);
      setSaveMessage(trigger === 'manual' ? 'Сохраняем...' : 'Автосохранение...');

      try {
        const response = await apiClient.put<ProjectResponse>(`/projects/${id}/sync`, {
          version: snapshot.version,
          document: snapshot.document,
        });

        const hasNewChanges = projectRef.current?.document !== snapshot.document;
        setProject((currentProject) => {
          if (!currentProject) {
            return currentProject;
          }

          const nextDocument = hasNewChanges
            ? currentProject.document
            : normalizeDocument(response.data.document);

          return {
            ...currentProject,
            version: response.data.version,
            document: nextDocument,
          };
        });

        setIsDirty(hasNewChanges);
        if (hasNewChanges) {
          setSaveMessage('Изменения продолжаются, готовим следующее автосохранение.');
        } else if (hideSuccess) {
          setSaveMessage(null);
        } else {
          setSaveMessage('Сохранено.');
        }
        autosaveBlockedRef.current = false;
      } catch (error) {
        const statusCode = error instanceof AxiosError ? error.response?.status : undefined;
        if (statusCode === 409 || statusCode === 422) {
          autosaveBlockedRef.current = true;
        }
        setIsDirty(true);
        setSaveMessage(getSyncErrorMessage(error));
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);

        if (pendingSyncRef.current) {
          const nextTrigger = pendingTriggerRef.current;
          pendingSyncRef.current = false;

          if (autosaveBlockedRef.current && nextTrigger === 'auto') {
            setSaveMessage(
              'Автосохранение остановлено из-за ошибки синхронизации. Исправьте данные и сохраните этап вручную.',
            );
            return;
          }

          void runSync(nextTrigger);
        }
      }
    },
    [id],
  );

  const enqueueSync = useCallback(
    (trigger: SaveTrigger) => {
      if (!projectRef.current || !id) {
        return;
      }

      if (autosaveBlockedRef.current && trigger === 'auto') {
        return;
      }

      if (isSavingRef.current) {
        pendingSyncRef.current = true;
        pendingTriggerRef.current = trigger === 'manual' ? 'manual' : pendingTriggerRef.current;
        return;
      }

      pendingTriggerRef.current = 'auto';
      void runSync(trigger);
    },
    [id, runSync],
  );

  useEffect(() => {
    if (!id) {
      setErrorMessage('Некорректный адрес проекта.');
      setIsLoading(false);
      return;
    }

    async function loadProject() {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await apiClient.get<ProjectResponsePayload>(`/projects/${id}`);
        setProject({
          ...response.data,
          document: normalizeDocument(response.data.document),
        });
        setIsDirty(false);
        setSaveMessage(null);
        autosaveBlockedRef.current = false;
        pendingSyncRef.current = false;
        pendingTriggerRef.current = 'auto';
      } catch (error) {
        setProject(null);
        setErrorMessage(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    }

    void loadProject();
  }, [id]);

  const activeTab = useMemo(() => {
    if (!project) {
      return 'object' as Stage;
    }

    return normalizeTab(project.document.currentTab);
  }, [project]);

  const tabStates = useMemo(() => {
    const completedStages = project ? project.document.completedStages : [];
    const invalidatedFromStage = project ? project.document.invalidatedFromStage : null;

    return STAGE_ORDER.map((stage) => ({
      stage,
      isActive: activeTab === stage,
      isCompleted: completedStages.includes(stage),
      isUnlocked: isStageOpen(stage, completedStages, invalidatedFromStage),
      needsConfirm: stageNeedsConfirm(stage, invalidatedFromStage),
    }));
  }, [activeTab, project]);
  const activeTabState = useMemo(
    () => tabStates.find((state) => state.stage === activeTab),
    [activeTab, tabStates],
  );
  const stageToSave =
    project && project.document.completedStages.length < STAGE_ORDER.length
      ? STAGE_ORDER[project.document.completedStages.length]
      : null;
  const canSaveActiveStage = Boolean(
    activeTabState &&
      activeTabState.isUnlocked &&
      !activeTabState.isCompleted &&
      stageToSave === activeTab,
  );
  const canEditActiveStage = Boolean(activeTabState?.isCompleted);
  const canvasViewOnly = activeTab === 'object' && canEditActiveStage;
  const saveStatusText = isSaving ? 'Сохраняем изменения...' : saveMessage ?? '';
  const saveStatusIsError =
    saveStatusText.includes('Конфликт') ||
    saveStatusText.includes('Ошибка') ||
    saveStatusText.includes('остановлено');

  useEffect(() => {
    if (!project || !id || !isDirty) {
      return;
    }

    if (autosaveBlockedRef.current) {
      return;
    }

    clearAutosaveTimer();
    autosaveTimerRef.current = setTimeout(() => {
      enqueueSync('auto');
    }, 2000);

    return clearAutosaveTimer;
  }, [clearAutosaveTimer, enqueueSync, id, isDirty, project]);

  useEffect(() => {
    function handleSaveHotkey(event: KeyboardEvent) {
      const isSaveShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's';
      if (!isSaveShortcut) {
        return;
      }

      event.preventDefault();
      clearAutosaveTimer();
      autosaveBlockedRef.current = false;
      enqueueSync('manual');
    }

    window.addEventListener('keydown', handleSaveHotkey);
    return () => window.removeEventListener('keydown', handleSaveHotkey);
  }, [clearAutosaveTimer, enqueueSync]);

  useEffect(() => clearAutosaveTimer, [clearAutosaveTimer]);

  function handleSelectTab(tab: Stage) {
    const currentTabState = tabStates.find((state) => state.stage === tab);
    if (!currentTabState?.isUnlocked) {
      return;
    }

    let hasChanged = false;
    setProject((currentProject) => {
      if (!currentProject) {
        return currentProject;
      }
      if (currentProject.document.currentTab === tab) {
        return currentProject;
      }
      hasChanged = true;

      return {
        ...currentProject,
        document: {
          ...currentProject.document,
          currentTab: tab,
        },
      };
    });

    if (hasChanged) {
      setIsDirty(true);
      setSaveMessage(null);
    }
  }

  function handleSaveStage() {
    if (!project || !activeTabState || !activeTabState.isUnlocked || activeTabState.isCompleted) {
      return;
    }
    if (isSavingRef.current) {
      setSaveMessage('Сохранение уже выполняется. Повторите после завершения текущего sync.');
      return;
    }

    const activeIndex = STAGE_ORDER.indexOf(activeTabState.stage);
    const nextStageIndex = project.document.completedStages.length;
    if (activeIndex < 0 || activeIndex !== nextStageIndex) {
      return;
    }

    const nextCompletedStages = STAGE_ORDER.slice(0, activeIndex + 1);
    const nextCurrentTab = getNextStage(activeTabState.stage);

    const nextProject: ProjectResponse = {
      ...project,
      document: {
        ...project.document,
        completedStages: nextCompletedStages,
        currentTab: nextCurrentTab,
        invalidatedFromStage: invalidatedAfterSave(
          activeTabState.stage,
          project.document.invalidatedFromStage,
        ),
      },
    };
    projectRef.current = nextProject;
    setProject(nextProject);

    setIsDirty(true);
    setSaveMessage(null);
    hideAdvancedStageSuccessRef.current = nextCurrentTab !== activeTabState.stage;
    autosaveBlockedRef.current = false;
    clearAutosaveTimer();
    enqueueSync('manual');
  }

  function handleEditStage() {
    if (!project || !activeTabState?.isCompleted) {
      return;
    }

    if (isSavingRef.current) {
      setSaveMessage('Сохранение уже выполняется. Повторите после завершения текущего sync.');
      return;
    }

    const confirmed = window.confirm(
      'Редактирование снимет подтверждение этого этапа и следующих. Введённые данные сохранятся. Продолжить?',
    );
    if (!confirmed) {
      return;
    }

    const nextProject: ProjectResponse = {
      ...project,
      document: {
        ...project.document,
        currentTab: activeTabState.stage,
        completedStages: getRollbackCompletedStages(activeTabState.stage),
        invalidatedFromStage: null,
      },
    };
    projectRef.current = nextProject;
    setProject(nextProject);

    setIsDirty(true);
    setSaveMessage(null);
    autosaveBlockedRef.current = false;
    clearAutosaveTimer();
    enqueueSync('manual');
  }

  return (
    <main className="editor-page">
      {isLoading ? <p>Загружаем проект...</p> : null}

      {!isLoading && errorMessage ? (
        <p role="alert" style={{ color: '#b91c1c' }}>
          {errorMessage}
        </p>
      ) : null}

      {!isLoading && project ? (
        <section className="editor-page__body">
          <div className="editor-actions-block">
            <div className="editor-actions">
              <h1 className="editor-project-name">{project.document.name}</h1>
              <button
                type="button"
                className="btn editor-save"
                onClick={handleSaveStage}
                disabled={!canSaveActiveStage || isSaving}
                title={
                  !canSaveActiveStage && stageToSave && activeTab !== stageToSave
                    ? `Сначала сохраните этап «${TAB_LABELS[stageToSave]}»`
                    : undefined
                }
              >
                {isSaving ? 'Сохраняем этап...' : 'Сохранить этап'}
              </button>
              {canEditActiveStage ? (
                <button
                  type="button"
                  className="btn editor-save"
                  onClick={handleEditStage}
                  disabled={isSaving}
                >
                  Редактировать этап
                </button>
              ) : null}
              {canvasViewOnly ? (
                <small className="editor-save-status">Чертёж только для просмотра</small>
              ) : null}
              {saveStatusText && !saveStatusIsError ? (
                <small className="editor-save-status">{saveStatusText}</small>
              ) : null}
            </div>
            {saveStatusIsError ? (
              <small className="editor-save-status editor-save-status--error">{saveStatusText}</small>
            ) : null}
          </div>

          <div className="editor-workspace">
            <div className="editor-tabs">
              {tabStates.map(({ stage, isActive, isCompleted, isUnlocked, needsConfirm }) => {
                const showLock = !isUnlocked;
                const showWarning = needsConfirm && isUnlocked;
                const tabTitle = showLock && stageToSave
                  ? `Сначала сохраните этап „${TAB_LABELS[stageToSave]}“`
                  : showWarning
                    ? 'Нужно подтвердить'
                    : undefined;

                return (
                  <button
                    key={stage}
                    type="button"
                    className={
                      showLock
                        ? 'editor-tab editor-tab--locked'
                        : isActive
                          ? 'editor-tab editor-tab--current'
                          : 'editor-tab'
                    }
                    onClick={isUnlocked ? () => handleSelectTab(stage) : undefined}
                    title={tabTitle}
                    data-stage={stage}
                    data-stage-active={isActive}
                    data-stage-completed={isCompleted}
                    data-stage-unlocked={isUnlocked}
                    data-stage-needs-confirm={needsConfirm}
                  >
                    {TAB_LABELS[stage]}
                    {isCompleted ? <span aria-hidden="true">✓</span> : null}
                    {showWarning ? <StageWarningIcon /> : null}
                    {showLock ? <StageLockIcon /> : null}
                  </button>
                );
              })}
            </div>

            <section className="editor-stage">
              {activeTab === 'object' ? (
                <ObjectCanvas
                  key={id}
                  floorLabel={readFloorLabel(project.document.floors)}
                  view={objectView}
                  onViewChange={setObjectView}
                  viewOnly={canvasViewOnly}
                />
              ) : (
                <p style={{ margin: 0 }}>{TAB_PLACEHOLDERS[activeTab]}</p>
              )}
            </section>
          </div>
        </section>
      ) : null}
    </main>
  );
}
